/**
 * @module browser-storage
 * @description Centralizes guarded access to browser storage for local-first
 * adapter mocks so SSR and non-DOM runtimes fail through typed adapter errors.
 */

import { AdapterInvariantError } from "./errors";

export type BrowserStorage = Pick<
  Storage,
  "clear" | "getItem" | "setItem" | "removeItem"
>;

type BrowserStorageGlobal = typeof globalThis & {
  localStorage?: Storage;
};

function getBrowserStorageRef(): Storage | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  return (
    window.localStorage ?? (globalThis as BrowserStorageGlobal).localStorage
  );
}

export function hasBrowserLocalStorage(): boolean {
  return getBrowserStorageRef() !== undefined;
}

export function getBrowserLocalStorage(): BrowserStorage {
  const storage = getBrowserStorageRef();
  if (!storage) {
    throw new AdapterInvariantError(
      "Browser storage is unavailable in this runtime.",
    );
  }
  return storage;
}

export function isBrowserStorageQuotaError(error: unknown): boolean {
  return (
    typeof DOMException !== "undefined" &&
    error instanceof DOMException &&
    error.name === "QuotaExceededError"
  );
}
